import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, update, remove } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaEye, FaFileAlt, FaTrash, FaCopy, FaFileExcel, FaPrint, FaUserTie, FaFilePdf, FaDice } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const STATUS_COLORS = {
    Approved:  { dot: 'bg-green-500',  text: 'text-green-700',  bg: 'bg-green-50',  border: 'border-green-200',  label: 'Accepted' },
    Rejected:  { dot: 'bg-red-500',    text: 'text-red-700',    bg: 'bg-red-50',    border: 'border-red-200',    label: 'Rejected' },
    Pending:   { dot: 'bg-orange-400', text: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Pending' },
};

const StatusBadge = ({ status }) => {
    const s = STATUS_COLORS[status] || STATUS_COLORS.Pending;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold border ${s.bg} ${s.text} ${s.border}`}>
            <span className={`w-2 h-2 rounded-full ${s.dot}`}></span>
            {s.label}
        </span>
    );
};

// Detail Modal
const DetailModal = ({ isOpen, onClose, data, onStatusUpdate }) => {
    const [ceoStatus, setCeoStatus] = useState('');
    const [remark, setRemark] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (data) {
            setCeoStatus(data.ceoStatus || 'Pending');
            setRemark(data.ceoRemark || '');
        }
    }, [data]);

    if (!isOpen || !data) return null;

    const handleSave = async () => {
        setSaving(true);
        try {
            await onStatusUpdate(data.id, { ceoStatus, ceoRemark: remark });
            onClose();
        } catch (e) {
            console.error(e);
        }
        setSaving(false);
    };

    const fields = [
        ['पूर्ण नाव', `${data.firstName || ''} ${data.middleName || ''} ${data.surname || ''}`],
        ['फोन नंबर', data.whatsappNo || data.mobileNo || '-'],
        ['ई-मेल', data.email || '-'],
        ['तालुका', data.taluka || '-'],
        ['पात्रता/शिक्षण', data.technicalQualification || '-'],
        ['नोंदणी दिनांक', data.submittedAt ? new Date(data.submittedAt).toLocaleDateString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'],
        ['Assistant Status', data.assistantStatus || 'Pending'],
        ['Accountant Status', data.accountantStatus || 'Pending'],
        ['Engineer Status', data.engineerStatus || 'Pending'],
        ['CEO Status', data.ceoStatus || 'Pending'],
        ['Admin Status', data.adminStatus || 'Pending'],
    ];

    return (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="bg-gradient-to-r from-[#2d2360] to-blue-700 px-6 py-4 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-white text-lg font-bold">अर्जदाराची संपूर्ण माहिती (लॉटरी)</h2>
                    <button onClick={onClose} className="text-white/80 hover:text-white text-2xl font-bold">×</button>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-2 gap-3 mb-5">
                        {fields.map(([label, value]) => (
                            <div key={label} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</div>
                                <div className="text-sm font-semibold text-gray-800">{value}</div>
                            </div>
                        ))}
                    </div>

                    <div className="border-t pt-5">
                        <h3 className="text-sm font-bold text-gray-700 mb-3">CEO Status अपडेट करा</h3>
                        <div className="flex flex-col gap-3">
                            <select
                                value={ceoStatus}
                                onChange={e => setCeoStatus(e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold outline-none focus:border-blue-500"
                            >
                                <option value="Pending">Pending (Pending)</option>
                                <option value="Approved">Approved (Accepted)</option>
                                <option value="Rejected">Rejected (Rejected)</option>
                            </select>
                            <textarea
                                rows={3}
                                placeholder="शेरा / टिप्पणी (ऐच्छिक)..."
                                value={remark}
                                onChange={e => setRemark(e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500"
                            />
                            <div className="flex gap-3">
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="flex-1 bg-[#2d2360] hover:bg-blue-900 text-white py-2 rounded-lg text-sm font-bold transition"
                                >
                                    {saving ? 'जतन करत आहे...' : 'जतन करा'}
                                </button>
                                <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-lg text-sm font-bold transition">
                                    रद्द करा
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CeoEngineerLotteryList = () => {
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);

    const navigate = useNavigate();
    const tableRef = useRef(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(user => {
            if (!user) navigate('/login');
        });

        const registrationsRef = ref(db, 'ekamwatapzp/EngineerLotteryRegistrations');
        const unsubData = onValue(registrationsRef, snapshot => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(key => ({ id: key, ...data[key] }));
                setRegistrations(list.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0)));
            } else {
                setRegistrations([]);
            }
            setLoading(false);
        });

        return () => { unsubscribe(); unsubData(); };

    }, [navigate]);

    const handleStatusUpdate = async (id, updateData) => {
        const itemRef = ref(db, `ekamwatapzp/EngineerLotteryRegistrations/${id}`);
        await update(itemRef, updateData);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('हा रेकॉर्ड डिलीट करायचा का?')) return;
        await remove(ref(db, `ekamwatapzp/EngineerLotteryRegistrations/${id}`));
    };

    const filteredData = registrations.filter(item => {
        const fullName = `${item.firstName || ''} ${item.middleName || ''} ${item.surname || ''}`.toLowerCase();
        const phone = (item.whatsappNo || item.mobileNo || '').toLowerCase();
        const term = searchTerm.toLowerCase();
        return fullName.includes(term) || phone.includes(term);
    });

    // Copy to clipboard
    const handleCopy = () => {
        const rows = filteredData.map((item, i) =>
            [
                i + 1,
                `${item.firstName || ''} ${item.middleName || ''} ${item.surname || ''}`,
                item.whatsappNo || item.mobileNo || '-',
                item.submittedAt ? new Date(item.submittedAt).toLocaleDateString('en-IN') : '-',
                item.assistantStatus || 'Pending',
                item.accountantStatus || 'Pending',
                item.engineerStatus || 'Pending',
                item.ceoStatus || 'Pending',
                item.adminStatus || 'Pending',
            ].join('\t')
        ).join('\n');
        navigator.clipboard.writeText(rows);
    };

    // Export to Excel
    const handleExcel = () => {
        const wsData = [
            ['क्र.', 'अर्जदाराचे नाव', 'फोन क्र.', 'नोंदणी दिनांक', 'Assistant Status', 'Accountant Status', 'Engineer Status', 'CEO Status', 'Admin Status'],
            ...filteredData.map((item, i) => [
                i + 1,
                `${item.firstName || ''} ${item.middleName || ''} ${item.surname || ''}`,
                item.whatsappNo || item.mobileNo || '-',
                item.submittedAt ? new Date(item.submittedAt).toLocaleDateString('en-IN') : '-',
                item.assistantStatus || 'Pending',
                item.accountantStatus || 'Pending',
                item.engineerStatus || 'Pending',
                item.ceoStatus || 'Pending',
                item.adminStatus || 'Pending',
            ])
        ];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'लॉटरी अर्ज यादी');
        XLSX.writeFile(wb, 'सुबे_अभियंता_लॉटरी_यादी.xlsx');
    };

    // Print
    const handlePrint = () => window.print();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
                {/* Page Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-5">
                    <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <FaDice className="text-blue-600" /> सु. बे. अभियंता लॉटरी अर्ज यादी
                    </h1>
                    <div className="text-sm text-gray-500">ठाणे जिल्हा परिषद / लॉटरी अर्ज यादी</div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {/* Legend + Toolbar Row */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between px-4 pt-4 pb-3 gap-3 border-b border-gray-100">
                        {/* Status Legend */}
                        <div className="flex items-center gap-4 text-xs font-semibold text-gray-600">
                            <span className="flex items-center gap-1.5">
                                <span className="w-3 h-3 rounded-full bg-orange-400 inline-block"></span> Pending
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span> Accepted
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="w-3 h-3 rounded-full bg-red-500 inline-block"></span> Rejected
                            </span>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 font-medium">{filteredData.length} अर्जाची संपूर्ण माहिती</span>
                        </div>
                    </div>

                    {/* Copy / Excel / Print + Search */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between px-4 py-3 gap-3">
                        <div className="flex gap-2">
                            <button onClick={handleCopy} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 rounded transition">
                                <FaCopy /> Copy
                            </button>
                            <button onClick={handleExcel} className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-3 py-1.5 rounded transition">
                                <FaFileExcel /> Excel
                            </button>
                            <button onClick={handlePrint} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-3 py-1.5 rounded transition">
                                <FaPrint /> Print
                            </button>
                        </div>

                        <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                            <input
                                type="text"
                                placeholder="Search:"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="pl-8 pr-4 py-1.5 border border-gray-200 rounded text-sm outline-none focus:border-blue-400 w-52"
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto" ref={tableRef}>
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-xs font-semibold">
                                    <th className="px-3 py-3 text-center border-r border-gray-100 w-10">क्र. ↑</th>
                                    <th className="px-3 py-3 text-left border-r border-gray-100">अर्जदाराचे नाव ↑↓</th>
                                    <th className="px-3 py-3 text-left border-r border-gray-100">फोन क्र. ↑↓</th>
                                    <th className="px-3 py-3 text-left border-r border-gray-100 whitespace-nowrap">नोंदणी<br/>दिनांक ↑↓</th>
                                    <th className="px-3 py-3 text-center border-r border-gray-100 whitespace-nowrap">Assistant<br/>Status ↑↓</th>
                                    <th className="px-3 py-3 text-center border-r border-gray-100 whitespace-nowrap">Accountant<br/>Status ↑↓</th>
                                    <th className="px-3 py-3 text-center border-r border-gray-100 whitespace-nowrap">Engineer<br/>Status ↑↓</th>
                                    <th className="px-3 py-3 text-center border-r border-gray-100 whitespace-nowrap">CEO<br/>Status ↑↓</th>
                                    <th className="px-3 py-3 text-center border-r border-gray-100 whitespace-nowrap">ADD<br/>CEO ↑↓</th>
                                    <th className="px-3 py-3 text-center">Action ↑↓</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredData.map((item, index) => (
                                    <tr key={item.id} className="hover:bg-blue-50/30 transition-colors">
                                        <td className="px-3 py-3 text-center text-gray-500 font-medium border-r border-gray-100">{index + 1}</td>
                                        <td className="px-3 py-3 font-semibold text-blue-700 border-r border-gray-100">
                                            {item.firstName} {item.middleName} {item.surname}
                                        </td>
                                        <td className="px-3 py-3 text-gray-700 border-r border-gray-100">
                                            {item.whatsappNo || item.mobileNo || '-'}
                                        </td>
                                        <td className="px-3 py-3 text-gray-600 border-r border-gray-100 whitespace-nowrap">
                                            {item.submittedAt
                                                ? new Date(item.submittedAt).toLocaleDateString('en-IN', {
                                                    day: '2-digit', month: 'short', year: 'numeric'
                                                  })
                                                : '-'}
                                        </td>
                                        <td className="px-3 py-3 text-center border-r border-gray-100">
                                            <StatusBadge status={item.assistantStatus || 'Pending'} />
                                        </td>
                                        <td className="px-3 py-3 text-center border-r border-gray-100">
                                            <StatusBadge status={item.accountantStatus || 'Pending'} />
                                        </td>
                                        <td className="px-3 py-3 text-center border-r border-gray-100">
                                            <StatusBadge status={item.engineerStatus || 'Pending'} />
                                        </td>
                                        <td className="px-3 py-3 text-center border-r border-gray-100">
                                            <StatusBadge status={item.ceoStatus || 'Pending'} />
                                        </td>
                                        <td className="px-3 py-3 text-center border-r border-gray-100">
                                            <StatusBadge status={item.adminStatus || 'Pending'} />
                                        </td>
                                        <td className="px-3 py-3">
                                            <div className="flex items-center justify-center gap-2">
                                                {/* View Detail */}
                                                <button
                                                    onClick={() => { setSelectedItem(item); setIsDetailOpen(true); }}
                                                    title="अर्जदाराची संपूर्ण माहिती"
                                                    className="p-1.5 rounded hover:bg-blue-100 text-blue-600 transition"
                                                >
                                                    <FaUserTie className="text-sm" />
                                                </button>
                                                {/* Documents */}
                                                <button
                                                    onClick={() => navigate('/ceo/verify-documents', { state: { selectedUser: { ...item, type: 'नवीन' } } })}
                                                    title="कामदपत्रे पहा"
                                                    className="p-1.5 rounded hover:bg-amber-100 text-amber-600 transition"
                                                >
                                                    <FaFileAlt className="text-sm" />
                                                </button>
                                                {/* Delete */}
                                                <button
                                                    onClick={() => handleDelete(item.id)}
                                                    title="डिलीट करा"
                                                    className="p-1.5 rounded hover:bg-red-100 text-red-500 transition"
                                                >
                                                    <FaTrash className="text-sm" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredData.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className="text-center py-12 text-gray-400 font-medium">
                                            कोणताही अर्ज आढळला नाही
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer Count */}
                    <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
                        एकूण {filteredData.length} नोंदी दाखवत आहे
                    </div>
                </div>

                {/* Detail Modal */}
                <DetailModal
                    isOpen={isDetailOpen}
                    onClose={() => setIsDetailOpen(false)}
                    data={selectedItem}
                    onStatusUpdate={handleStatusUpdate}
                />

            </div>
    );
};

export default CeoEngineerLotteryList;
